import React, { useCallback } from 'react';
import { View } from 'react-native';
import { observer } from 'mobx-react-lite';
import ResponsiveScaleProvider from '../../contexts/ResponsiveScaleProvider';
import FullscreenPreview from '@photosi/rn-components/product-preview/fullscreen-preview/FullscreenPreview';
import { useStore } from '../../contexts/StoreContext';
import { PREVIEW_PRODUCT_TYPES } from 'src/store/_models/preview.constants';
import { ProductPreviewProps } from './ProductPreview.types';
import { Preview } from './preview/Preview';
import { ContainerPreview } from './ContainerPreview';
import { useResponsiveScale } from '../../contexts/ResponsiveScaleContext';
import { SharedValue, useSharedValue } from 'react-native-reanimated';
import { UserTextModelInstance } from '../../store/_models/userText';

export const ProductPreview = observer(
  ({
    productType = PREVIEW_PRODUCT_TYPES.FRAME,
    preview,
    editable = false,
    swappable = false,
    showGrid = false,
    shownFullScreen = false,
    disabled = false,
    disableSwapImageAnimation = false,
    renderOverlays,
    shouldUseContainerDimensions = false,
    showOverlays = false,
    isHighlighted = false,
    showBadges = true,
    isDropdownOpen = false,
    userTexts = [],
  }: ProductPreviewProps) => {
    const {
      project: { activeUserImageArea },
    } = useStore();

    const previewViewBox = {
      width: preview.viewBox.width,
      height: preview.viewBox.height,
    };
    const activeUserImageAreaViewBox = {
      width: activeUserImageArea?.width as number,
      height: activeUserImageArea?.height as number,
    };

    const isFullScreenPreview = shownFullScreen && activeUserImageArea;
    const containerViewBox = isFullScreenPreview ? previewViewBox : undefined;
    const viewBox = isFullScreenPreview ? activeUserImageAreaViewBox : previewViewBox;

    // State for direct integration mode with SharedValue
    const containerDimensionsSV = useSharedValue<{ width: number; height: number }>({
      width: 0,
      height: 0,
    });

    // Callback to receive SharedValue from ContainerPreview
    const handleContainerDimensionsChange = useCallback((dimensions: { width: number; height: number }) => {
      containerDimensionsSV.value = dimensions;
    }, []);

    return (
      <>
        <ContainerPreview
          shouldUseContainerDimensions={shouldUseContainerDimensions}
          onContainerDimensionsChange={handleContainerDimensionsChange}
        >
          <ResponsiveScaleProvider
            containerViewBox={containerViewBox}
            viewBox={viewBox}
            containerDimensionsSV={shouldUseContainerDimensions ? containerDimensionsSV : undefined}
          >
            <StablePreviewRenderer
              isFullScreenPreview={!!isFullScreenPreview}
              preview={preview}
              disabled={disabled}
              editable={editable}
              showGrid={showGrid}
              disableSwapImageAnimation={disableSwapImageAnimation}
              productType={productType}
              swappable={swappable}
              renderOverlays={renderOverlays}
              showOverlays={showOverlays}
              isHighlighted={isHighlighted}
              showBadges={showBadges}
              isDropdownOpen={isDropdownOpen}
              userTexts={userTexts}
            />
          </ResponsiveScaleProvider>
        </ContainerPreview>
      </>
    );
  },
);

// Internal component that handles stable rendering based on scale readiness
const StablePreviewRenderer = observer(
  ({
    isFullScreenPreview,
    preview,
    disabled,
    editable,
    showGrid,
    disableSwapImageAnimation,
    productType,
    swappable,
    renderOverlays,
    showOverlays,
    isHighlighted,
    showBadges,
    userTexts,
  }: {
    isFullScreenPreview: boolean;
    preview: any; // TODO: sistemare i tipi
    disabled: boolean;
    editable: boolean;
    showGrid: boolean;
    disableSwapImageAnimation: boolean;
    productType: string;
    swappable: boolean;
    renderOverlays?: any;
    showOverlays: boolean;
    isHighlighted: any;
    showBadges: boolean;
    userTexts: UserTextModelInstance[];
  }) => {
    const { responsiveScale } = useResponsiveScale();

    // Wait for scale to be ready before rendering to prevent flash
    if (responsiveScale <= 0) {
      return <View style={{ opacity: 0, width: '100%', height: '100%' }} />;
    }

    // Render actual content when scale is stable
    return isFullScreenPreview ? (
      <FullscreenPreview
        preview={preview}
        disabled={disabled}
        editable={editable}
        showGrid={showGrid}
        disableSwapImageAnimation={disableSwapImageAnimation}
      />
    ) : (
      <Preview
        productType={productType}
        editable={editable}
        showGrid={showGrid}
        swappable={swappable}
        disabled={disabled}
        preview={preview}
        disableSwapImageAnimation={disableSwapImageAnimation}
        renderOverlays={renderOverlays}
        showOverlays={showOverlays}
        isHighlighted={isHighlighted}
        showBadges={showBadges}
        userTexts={userTexts}
      />
    );
  },
);
